import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import Testimonials from "@user/components/Testimonials";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useExperiment } from "@/lib/experiment";
import { HeroSection } from "@user/components/home/HeroSection";
import { CategoriesSection, StatsSection } from "@user/components/home/CategoriesSection";
import { TopLawyersSection } from "@user/components/home/TopLawyersSection";
import { PricingSection, HowItWorksSection } from "@user/components/home/PricingSection";
import AuthenticatedHome from "@user/components/home/AuthenticatedHome";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [query, setQuery] = React.useState("");
  const [topLawyers, setTopLawyers] = useState([]);
  const [lawStats, setLawStats] = useState({ online: 12, avg_response_mins: 2 });

  // A/B test: chat entry price (₹99 vs ₹199)
  const { variant, track } = useExperiment("chat_entry_price", ["A", "B"]);
  const chatPrice = variant === "B" ? 199 : 99;
  const consultPrice = variant === "B" ? 399 : 299;

  // A/B test: CTA copy (Consult Now vs Book Now)
  const { variant: ctaVariant, track: trackCta } = useExperiment("cta_copy", ["consult_now", "book_now"]);
  const ctaLabel = ctaVariant === "book_now" ? "Book Now" : "Consult Now";

  // A/B test: hero headline
  const { variant: headlineVariant, track: trackHeadline } = useExperiment("hero_headline", ["talk_to_lawyer", "legal_help_5min"]);

  useEffect(() => {
    if (user) return;
    api.get("/lawyers", { params: { available_only: true } })
      .then((r) => setTopLawyers((r.data || []).slice(0, 4)))
      .catch(() => setTopLawyers([]));
    api.get("/lawyers-stats").then((r) => setLawStats(r.data)).catch(() => {});
  }, [user]);

  // Scroll to hash anchor when navigating to /#pricing or /#how-it-works
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [location.hash]);

  const onAsk = () => {
    if (query.trim()) sessionStorage.setItem("adv_initial_q", query.trim());
    navigate("/chat");
  };

  const onBook = (l) => {
    track("cta_click");
    trackCta("cta_click");
    trackHeadline("cta_click");
    if (!user) { login(); return; }
    navigate(`/book/${l.id}?type=chat`);
  };


  // Hero / pricing / how-it-works CTAs fire the same experiment events.
  const onCta = () => { track("cta_click"); trackCta("cta_click"); trackHeadline("cta_click"); };
  const onPricingCta = () => { track("cta_click"); trackCta("cta_click"); };

  // Authenticated users get the redesigned app homepage; public visitors keep the existing marketing page.
  if (user) return <AuthenticatedHome />;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Header />

      <HeroSection headlineVariant={headlineVariant} chatPrice={chatPrice} consultPrice={consultPrice} ctaLabel={ctaLabel} lawStats={lawStats}
        query={query} setQuery={setQuery} onAsk={onAsk} onCta={onCta} onPreviewBook={() => { track("cta_click"); navigate("/lawyers"); }} navigate={navigate} />

      <CategoriesSection />

      <TopLawyersSection topLawyers={topLawyers} onBook={onBook} />

      <PricingSection ctaLabel={ctaLabel} onCta={onPricingCta} />

      <HowItWorksSection ctaLabel={ctaLabel} onCta={onPricingCta} />

      <StatsSection />

      {/* TESTIMONIALS */}
      <Testimonials />

      <Footer />

      {/* Sticky mobile bottom CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200 p-3 shadow-2xl" data-testid="mobile-sticky-cta">
        <Link to="/lawyers" className="block" onClick={onCta}>
          <Button className="w-full min-h-[48px] rounded-full adv-btn-gold font-bold flex flex-col items-center leading-tight gap-0 py-2">
            <span className="text-base">{ctaLabel}</span>
            <span className="text-[11px] font-semibold opacity-75">Starting ₹{chatPrice}</span>
          </Button>
        </Link>
      </div>
      <div className="md:hidden h-20" />
    </div>
  );
}
